import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import BannerAlcance, { esAcotada } from "../components/BannerAlcance";
import { useAuth } from "../contexts/AuthContext";
import { Link } from "react-router-dom";
import { usePlanActual } from "../lib/planes";
import { toast } from "sonner";
import { Bot, TrendingUp, Users, Calendar } from "lucide-react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

const PERIODS = [
  { label: "7 días", days: 7 },
  { label: "30 días", days: 30 },
  { label: "90 días", days: 90 },
];

function dayLabel(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  return `${d.getDate()}/${d.getMonth() + 1}`;
}

function SectionTitle({ children }) {
  return <h2 className="text-base font-semibold text-slate-800 mb-4">{children}</h2>;
}

function StatCard({ value, label, sub, accent = "blue" }) {
  const colors = {
    blue: "text-blue-700 bg-blue-50",
    green: "text-emerald-700 bg-emerald-50",
    violet: "text-violet-700 bg-violet-50",
  };
  return (
    <div className={`rounded-xl p-4 ${colors[accent]}`}>
      <div className="text-2xl font-bold">{value ?? "—"}</div>
      <div className="text-sm font-medium mt-0.5">{label}</div>
      {sub && <div className="text-xs opacity-70 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function Metricas() {
  const { activeClinic, puede} = useAuth();
  const { sinSofi } = usePlanActual(); // TEN.4: un plan Sistema no ve métricas de Sofi
  const [days, setDays] = useState(30);
  const [apptData, setApptData] = useState([]);
  const [sofi, setSofi] = useState(null);
  const [overview, setOverview] = useState(null);
  const [recentPatients, setRecentPatients] = useState([]);
  const [loading, setLoading] = useState(true);

  // Sin `agenda` los tres endpoints de métricas dan 403.
  const verMetricas = puede("agenda");

  const loadData = useCallback(() => {
    setLoading(true);
    // 🔴 Antes los cuatro iban en un mismo `Promise.all`, y eso mezclaba DOS capacidades:
    //    los tres `/metrics/*` exigen `agenda` y `/patients` exige `pacientes`. Con una
    //    sola que faltara, la promesa rechazaba ENTERA y la pantalla quedaba vacía con un
    //    toast genérico -- aunque la otra mitad de los datos sí fuera pedible. Cada
    //    familia va bajo su propia capacidad y con su propio fallo.
    const tareas = [];
    if (verMetricas) {
      tareas.push(
        Promise.all([
          api.get(`/metrics/appointments-by-day?days=${days}`),
          api.get(`/metrics/sofi-activity?days=${days}`),
          api.get(`/metrics/overview?days=${days}`),
        ]).then(([byDay, sofiAct, ov]) => {
          setApptData((byDay.data || []).map((d) => ({ ...d, label: dayLabel(d.date) })));
          setSofi(sofiAct.data);
          setOverview(ov.data);
        })
      );
    }
    if (puede("pacientes")) {
      tareas.push(
        api.get("/patients?limit=5").then((pats) =>
          setRecentPatients((pats.data?.items || []).slice(0, 5))
        )
      );
    }
    Promise.all(tareas)
      .catch(() => toast.error("Error al cargar métricas"))
      .finally(() => setLoading(false));
  }, [days, verMetricas, puede]);

  useEffect(() => { loadData(); }, [loadData]);

  // 🔴 NO SE SUMA, NO SE PROMEDIA Y NO SE DIVIDE AQUI (DT.263). Estas tres cifras salen
  //    calculadas de `/metrics/overview`, que es el unico sitio que sabe con que ALCANCE
  //    se calcularon: las filas de `appointments-by-day` ya vienen filtradas por la
  //    politica de COL.8, asi que sumarlas en el navegador y llamarlas "Total del
  //    periodo" presenta un PARCIAL como total. La cifra no estaba rota: estaba mal
  //    descrita, que es el modo de falla de DT.233.
  // ⚠️ Y de paso corrige un valor: el promedio se dividia entre los dias CON citas y no
  //    entre los del periodo, con la etiqueta "Promedio diario". La cifra nueva es mas
  //    baja porque la anterior estaba mal, no porque falte nada.
  const rango = overview?.rango;
  const acotado = overview?.alcance && overview.alcance.total === false;

  // `convRate` retirado con la "Tasa de conversion": su numerador era el literal 0.

  // 🔴 Aqui vivian DOS cifras hardcodeadas (auditoria de copy, 12 Sep 2026):
  //   · `planSlug = "starter"` -- le decia "starter" a TODA clinica, incluidas las de plan
  //     Pro o Growth. El plan real estaba a mano en la sesion y no se usaba.
  //   · `planLimit = 500` -- el tope de Starter, aplicado a todos. Para una clinica Pro
  //     (2,000) o Growth (ilimitado), la barra de consumo y el aviso "estas cerca de tu
  //     limite" eran falsos, y podian empujar a contratar un plan que ya se tenia.
  // El plan ahora sale de la sesion. La BARRA DE CONSUMO se retira: el limite real vive en
  // `vertical_plans.limits.max_conversations_per_month` (agency) y este panel no lo consulta;
  // ademas no hay enforcement de ese tope en este repo, asi que pintar un porcentaje de uso
  // seria inventar un techo. Cuando el panel lea el limite de verdad, se reconstruye.
  // ⚠️ CADUCADO en parte (LIM.5, 4 Oct 2026): el panel ya lee el limite real (`GET /api/consumo`,
  //    calculado en agency) y la barra vive en UN sitio, la seccion «Sofi este mes» del Dashboard
  //    (`components/ConsumoSofi.js`). Aqui NO se reintroduce: dos barras serian dos sitios que
  //    discrepan.
  const planSlug = activeClinic?.plan_slug || null;
  const convUsed = overview?.sofi_conversations_this_month ?? "—";

  return (
    <div className="p-6 lg:p-8 space-y-8 max-w-5xl mx-auto w-full">

      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Métricas de la clínica</h1>
          <p className="text-sm text-slate-500 mt-0.5">Resumen de actividad y rendimiento</p>
        </div>
        <div className="flex gap-2">
          {PERIODS.map((p) => (
            <button
              key={p.days}
              onClick={() => setDays(p.days)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                days === p.days
                  ? "bg-blue-600 text-white"
                  : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {!loading && overview?.alcance && <BannerAlcance alcance={overview.alcance} mezcla />}

      {!verMetricas && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="metricas-sin-agenda">
          <p className="font-semibold text-slate-900">Tu acceso no incluye la agenda</p>
          <p className="text-sm text-slate-600 mt-1">
            Las métricas de citas no son cifras que te correspondan, y mostrarlas en cero
            diría que la clínica no tuvo ninguna.
          </p>
        </div>
      )}

      {loading ? (
        <div className="space-y-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-48 bg-slate-100 rounded-2xl animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          {/* 🔴 Sin `agenda` estas dos secciones NO se pintan. Su estado vacio
              ("Sin citas en este período") es una afirmacion, y para quien no tiene la
              capacidad seria falsa: la clinica si tuvo citas, sólo que no le corresponde
              verlas. */}
          {verMetricas && (
          <>
          {/* SECCIÓN 1 — Citas */}
          <section className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="w-4 h-4 text-blue-600" />
              <SectionTitle>Citas — últimos {days} días</SectionTitle>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-6">
              <StatCard
                value={rango?.citas_total ?? "—"}
                label={esAcotada(overview?.alcance, "rango") ? "Tus citas en el período" : "Total del período"}
                accent="blue"
              />
              <StatCard value={rango?.citas_por_dia ?? "—"} label="Promedio diario" accent="blue" />
              <StatCard
                value={rango?.dia_pico ? `${rango.dia_pico.count} (${dayLabel(rango.dia_pico.date)})` : "—"}
                label="Día con más citas"
                accent="blue"
              />
            </div>
            {apptData.length === 0 ? (
              <div className="h-[280px] flex items-center justify-center text-slate-400 text-sm">Sin citas en este período</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={apptData} margin={{ top: 0, right: 0, bottom: 0, left: -20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94A3B8" }} interval={Math.floor(apptData.length / 8)} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip
                    cursor={{ fill: "#F1F5F9" }}
                    contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }}
                    formatter={(v) => [v, "Citas"]}
                  />
                  <Bar dataKey="count" fill="#2563EB" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </section>

          </>
          )}

          {/* SECCIÓN 2 — Pacientes */}
          <section className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-4 h-4 text-emerald-600" />
              <SectionTitle>Pacientes</SectionTitle>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-5">
              <StatCard
                value={overview?.patients_total}
                label="Total registrados"
                sub={acotado ? "de toda la clínica" : undefined}
                accent="green"
              />
              <StatCard
                value={overview?.patients_new_this_month}
                label="Nuevos este mes"
                sub={acotado ? "de toda la clínica" : undefined}
                accent="green"
              />
            </div>
            {recentPatients.length > 0 && (
              <div>
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Últimos registrados</div>
                <div className="divide-y divide-slate-100">
                  {recentPatients.map((p) => (
                    <div key={p.id} className="flex items-center justify-between py-2.5">
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold flex items-center justify-center">
                          {(p.name || "?")[0]}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-slate-800">{p.name}</div>
                          <div className="text-xs text-slate-400">{p.phone}</div>
                        </div>
                      </div>
                      <div className="text-xs text-slate-400">
                        {p.created_at ? new Date(p.created_at).toLocaleDateString("es-MX") : "—"}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* SECCIÓN 3 — Sofi */}
          {!sinSofi && (
          <section className="rounded-2xl border border-[#DDD6FE] p-5 shadow-sm" style={{ background: "#F5F3FF" }}>
            <div className="flex items-center gap-2 mb-4">
              <Bot className="w-4 h-4 text-violet-600" />
              <SectionTitle>Actividad de Sofi — últimos {days} días</SectionTitle>
            </div>
            {/* "Citas agendadas" y "Tasa de conversion" RETIRADAS (auditoria de copy, 12 Sep 2026).
                  Salian de `total_bookings`, que es un LITERAL 0 en el backend desde S6 (7 Ago 2026),
                  cuando se retiro la tabla `booking_bookings` sin censar quien la consumia. Llevaban
                  meses mostrando 0 y 0.0%: una clinica concluiria que Sofi no agenda nada. El
                  agendamiento real de Sofi hoy va a `appointments`, pero no hay forma de distinguirlo
                  del que crea el panel hasta que exista columna de origen -- ver CLAUDE.md. */}
            <div className="grid grid-cols-1 gap-3 mb-5">
              <StatCard
                value={sofi?.total_conversations}
                label="Conversaciones"
                sub={acotado ? "de toda la clínica" : undefined}
                accent="violet"
              />
            </div>
            {sofi?.conversations_by_day?.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={sofi.conversations_by_day.map((d) => ({ ...d, label: dayLabel(d.date) }))} margin={{ top: 0, right: 0, bottom: 0, left: -20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(167,139,250,0.2)" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#A78BFA" }} interval={Math.floor(sofi.conversations_by_day.length / 6)} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#A78BFA" }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip
                    cursor={{ stroke: "#DDD6FE" }}
                    contentStyle={{ borderRadius: 8, border: "1px solid #DDD6FE", fontSize: 12, background: "#fff" }}
                    formatter={(v) => [v, "Conv."]}
                  />
                  <Line type="monotone" dataKey="count" stroke="#7C3AED" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[100px] flex items-center justify-center text-violet-400 text-sm">Sin conversaciones en este período</div>
            )}
          </section>
          )}

          {/* SECCIÓN 4 — Estado del plan */}
          <section className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-4 h-4 text-slate-500" />
              <SectionTitle>Estado del plan</SectionTitle>
            </div>
            <div className="flex items-center justify-between mb-3">
              <div>
                <span className="text-sm font-semibold text-slate-700 capitalize">{planSlug || "—"}</span>
                <span className="ml-2 text-xs text-slate-400">Plan actual</span>
              </div>
              {verMetricas && !sinSofi && (
                <span className="text-xs text-slate-500">{convUsed} conversaciones este mes</span>
              )}
            </div>
            {/* TEN.4: apuntaba a clinicas.surit.ai/onboarding -- el formulario PÚBLICO con token,
                no la pantalla de planes. Un botón que no lleva a donde dice. */}
            <Link
              to="/app/onboarding"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:underline"
            >
              Ver planes disponibles
            </Link>
          </section>
        </>
      )}
    </div>
  );
}
